/* Escalera de precios de la PDP.
   El tema renderiza las variantes de cantidad como un <select>: el cliente
   ve "Combo: 3 Magnezen" sin precio y tiene que elegir a ciegas. Este
   modulo lee los datos que el propio tema ya deja en el DOM (cada <option>
   viene con el JSON de su variante) y dibuja tarjetas comparables con
   precio total, precio por unidad y ahorro real.

   No reemplaza al control del tema: al elegir una tarjeta se setea el
   <select> y se dispara su evento change, asi que el precio, la imagen y
   el formulario los sigue actualizando el tema. Si el producto no tiene
   escalera (o los datos no cuadran) no hace nada y el select queda visible
   y funcionando: el fallback es el comportamiento original.

   Los porcentajes NO se asumen: cada producto tiene su propia escalera
   (MagneZen 5/10%, Hai Liver 10/15%) y todo se calcula contra el precio
   de la variante unitaria. */

(function () {
  'use strict';

  function money(cents) {
    return '$' + Math.round(cents / 100).toLocaleString('es-CL');
  }

  /* Cantidad de unidades que representa una variante. Se exige un numero
     explicito en el titulo ("Combo: 3 X"); "Solo 1 X" cae en el mismo caso. */
  function qtyFromTitle(title) {
    var match = String(title || '').match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  /* Datos de la variante de cada <option>: el tema los deja en un
     <script type="application/json" data-resource="<id del option>">. */
  function variantData(root, option) {
    var node = root.querySelector('script[data-resource="' + option.id + '"]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /* Nombres que delatan una escalera de cantidad. Deliberadamente tolerante
     con la convencion ("Combo: 3 X", "Pack 3", "3 unidades", "3 meses"):
     las variantes se crean a mano en el admin y no siempre con el mismo
     formato. NO incluye "capsulas"/"mg": un producto con variantes de
     formato ("60 capsulas" / "120 capsulas") no es una escalera y calcular
     ahorros sobre eso seria inventar descuentos que no existen. */
  var ESCALERA = /combo|pack|\d+\s*(unidades?|frascos?|cajas?|potes?|tarros?|envases?|meses|mes)\b/i;

  /* Convierte las opciones en una escalera, o devuelve null si el producto
     no es una: sin variante unitaria de referencia no hay ahorro que
     calcular y es preferible no tocar nada. */
  function buildLadder(root, select) {
    if (root.dataset.optionsSize !== '1') return null;

    var options = Array.prototype.slice.call(select.options);
    if (options.length < 2) return null;
    if (!options.some(function (o) { return ESCALERA.test(o.value); })) return null;

    var steps = [];
    var seen = {};
    for (var i = 0; i < options.length; i++) {
      var data = variantData(root, options[i]);
      var qty = qtyFromTitle(options[i].value);
      if (!data || !qty || !data.price || seen[qty]) return null;
      seen[qty] = true;
      steps.push({
        value: options[i].value,
        qty: qty,
        price: data.price,
        available: data.available !== false
      });
    }

    var base = steps.filter(function (s) { return s.qty === 1; })[0];
    if (!base) return null;

    steps.sort(function (a, b) { return a.qty - b.qty; });
    steps.forEach(function (step) {
      step.unit = step.price / step.qty;
      step.saving = base.price * step.qty - step.price;
      step.percent = Math.round((step.saving / (base.price * step.qty)) * 100);
    });

    var best = steps.reduce(function (a, b) { return b.unit < a.unit ? b : a; });
    if (best.saving > 0) best.isBest = true;

    return steps;
  }

  /* La escalera se monta como hermana del picker, NO dentro: al cambiar de
     variante el tema reescribe el interior de <variant-selects> y borraria
     las tarjetas, dejando asomar el <select> original hasta que el observer
     las reconstruye (el parpadeo que se veia "mientras piensa"). El
     contenedor padre si sobrevive al re-render, asi que montada ahi la
     escalera no se destruye nunca y no hay nada que reconstruir. */
  /* Rótulo de cada tarjeta. Con la duración del envase declarada en custom.ficha (la imprime
     buy-buttons.liquid en data-hai-duracion, p. ej. "30 días") la escalera habla en meses:
     "1 mes · 3 meses · 6 meses" (Andrés, 16-sep-2026). Sin duración, o si no es múltiplo de
     30 días, vuelve a "1 unidad · 3 unidades": no se inventa un mes que el envase no cubre. */
  function diasDelEnvase(host) {
    var ambito = host.closest('.product__info-container') || document;
    var nodo = ambito.querySelector('[data-hai-duracion]');
    var m = nodo && String(nodo.dataset.haiDuracion || '').match(/(\d+)\s*d/i);
    return m ? parseInt(m[1], 10) : 0;
  }

  function rotulo(qty, dias) {
    if (dias && dias % 30 === 0) {
      var meses = (qty * dias) / 30;
      return meses === 1 ? '1 mes' : meses + ' meses';
    }
    return qty === 1 ? '1 unidad' : qty + ' unidades';
  }

  function render(root, select, steps) {
    var host = root.parentElement;
    var dias = diasDelEnvase(host);
    var list = document.createElement('div');
    list.className = 'hai-ladder';
    list.innerHTML =
      '<span class="hai-ladder__label">Cantidad</span>' +
      steps.map(function (step) {
        var unidades = rotulo(step.qty, dias);
        // 16-sep-2026 ("menos formas, un ritmo", acordado en la réplica "Ficha ideal Hai"):
        // el descuento va en texto rojo, no en insignia; la tarjeta de 1 unidad dice
        // "precio normal" donde las otras dicen el precio por unidad, así las tres comparten
        // estructura. Sin "Mejor precio por unidad": el −N % ya lo dice. El ahorro va en su
        // propio span: en móvil se oculta para que la tarjeta quepa en la fila de tres.
        var badge = step.percent > 0
          ? '<span class="hai-ladder__off">&minus;' + step.percent + ' %</span>'
          : '';
        var meta = step.qty === 1 ? 'precio normal' : money(step.unit) + ' c/u';
        if (step.qty !== 1 && step.saving > 0) meta += '<span class="hai-ladder__save"> &middot; ahorras ' + money(step.saving) + '</span>';
        var best = '';
        return (
          '<button type="button" class="hai-ladder__option" role="radio"' +
          ' data-value="' + String(step.value).replace(/"/g, '&quot;') + '"' +
          (step.available ? '' : ' disabled') + '>' +
          '<span class="hai-ladder__mark" aria-hidden="true"></span>' +
          '<span class="hai-ladder__body">' +
          '<span class="hai-ladder__top">' +
          '<span class="hai-ladder__qty">' + unidades + '</span>' + badge +
          '<span class="hai-ladder__price">' + money(step.price) + '</span>' +
          '</span>' +
          '<span class="hai-ladder__meta">' + meta + '</span>' + best +
          '</span>' +
          '</button>'
        );
      }).join('');

    list.setAttribute('role', 'radiogroup');
    var etiqueta = root.querySelector('label, legend');
    list.setAttribute('aria-label', etiqueta && etiqueta.textContent.trim() ? etiqueta.textContent.trim() : 'Cantidad');

    /* El <select> se busca fresco en cada clic: el tema lo reemplaza en cada
       re-render y una referencia capturada apuntaria a un nodo muerto. */
    list.addEventListener('click', function (event) {
      var option = event.target.closest('.hai-ladder__option');
      if (!option || option.disabled) return;
      var current = host.querySelector('variant-selects select');
      if (!current) return;
      current.value = option.dataset.value;
      current.dispatchEvent(new Event('change', { bubbles: true }));
      sync(list, current);
    });

    /* Teclado como un grupo de radios (25-oct-2026, tema B): una sola parada de Tab (la opción elegida, ver
       sync) y las flechas mueven la selección; Inicio y Fin van a la primera y la última. */
    list.addEventListener('keydown', function (event) {
      // Con una tecla mantenida (repeat) no se encadenan cambios: cada uno pide la sección al servidor y el tema no
      // cancela los pedidos viejos. Con Alt, Ctrl o Cmd la tecla es del navegador (Alt+flecha = atrás/adelante).
      if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
      var paso = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      var opciones = Array.prototype.filter.call(list.querySelectorAll('.hai-ladder__option'), function (o) { return !o.disabled; });
      var actual = opciones.indexOf(event.target.closest('.hai-ladder__option'));
      if (actual < 0 || !opciones.length) return;
      var destino;
      if (paso) destino = opciones[(actual + paso + opciones.length) % opciones.length];
      else if (event.key === 'Home') destino = opciones[0];
      else if (event.key === 'End') destino = opciones[opciones.length - 1];
      if (!destino) return;
      event.preventDefault();
      destino.focus();
      destino.click();
    });

    host.insertBefore(list, root);
    sync(list, select);
  }


  /* El tema puede cambiar la variante por su cuenta (deep link, sold out):
     el estado visual siempre se lee del select, que es la fuente de verdad. */
  function sync(list, select) {
    var opciones = list.querySelectorAll('.hai-ladder__option');
    // Parada de Tab: la elegida, o la primera disponible si la elegida está agotada (un enlace con ?variant= a un
    // combo sin stock) o si ninguna calza con el select. Un botón deshabilitado no recibe el foco: sin esto, con
    // el teclado no se llegaba a ninguna tarjeta.
    var habilitadas = Array.prototype.filter.call(opciones, function (o) { return !o.disabled; });
    var parada = habilitadas.filter(function (o) { return o.dataset.value === select.value; })[0] || habilitadas[0];
    Array.prototype.forEach.call(opciones, function (option) {
      var on = option.dataset.value === select.value;
      option.classList.toggle('is-selected', on);
      option.setAttribute('aria-checked', on ? 'true' : 'false');
      option.tabIndex = option === parada ? 0 : -1;
    });
  }

  /* La condicion de salida mira si las tarjetas estan de verdad en el DOM,
     nunca una marca propia: una marca sobrevive al re-render describiendo
     algo que el tema ya borro, y el CSS terminaria ocultando el <select>
     sin escalera que lo reemplace (PDP sin selector de cantidad). */
  function enhance(root) {
    var host = root.parentElement;
    if (!host || host.querySelector('.hai-ladder')) return;
    var select = root.querySelector('select');
    if (!select) return;

    var steps = buildLadder(root, select);
    if (!steps) return;

    render(root, select, steps);
  }

  function enhanceAll() {
    Array.prototype.forEach.call(document.querySelectorAll('variant-selects'), enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAll);
  } else {
    enhanceAll();
  }

  /* El select es la fuente de verdad del estado. Listener delegado: los
     selects van y vienen con cada re-render y uno por instancia se
     acumularia sobre nodos muertos. */
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select.matches || !select.matches('variant-selects select')) return;
    var root = select.closest('variant-selects');
    var host = root && root.parentElement;
    var list = host && host.querySelector('.hai-ladder');
    if (list) sync(list, select);
  });

  /* Reconstruir tras cada re-render del tema. Agrupado con un flag para no
     recorrer el DOM en cada mutacion (las nuestras incluidas). */
  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      enhanceAll();
    }, 0);
  }).observe(document.body, {
    childList: true,
    subtree: true
  });
})();
